import styled from '@emotion/styled'

import {
  BankUserCard,
  DownloadLink,
  FilterButton,
  MasterDetail,
  onDownloadLinkClick,
  RolesSelector,
  Section,
  Space,
  Typography
} from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import LinkIcon from '../../../components/core-v2/molecules/linkIcon'
import { Errors } from '../../tradebuilder-v2/util'
import { BankUser } from '../types'
import { UserDetailsPanel } from './bank-users-detail'

export type BankUsersLayoutProps = {
  availableRoles: string[]
  canDelete: boolean
  canProceed: boolean
  currentUser: string
  errors: Errors
  isFiltering: boolean
  isNew: boolean
  onChangeEmail: (name: string) => void
  onChangeFirstName: (name: string) => void
  onChangeLastName: (name: string) => void
  onChangeRole: (name: string) => void
  onChangeRoleFilter: (x: string) => void
  onChangeSearchTerm: (x: string) => void
  onChangeVerificationUser: (input: string, target: string) => void
  onClickFilter: () => void
  onDeleteUser: () => void
  onSaveUser: () => void
  onSelectUser: (userId: string) => void
  roleFilter: string[]
  saveSuccess: boolean
  searchTerm: string
  selectedUser?: BankUser
  serverErrors: string[]
  setIsNew: () => void
  totalNumUsers: number
  users: BankUser[]
  verificationUsername: string
}

const StyledContentSection = styled.section`
  height: calc(100vh - 119px);
`

const StyledInput = styled.input`
  width: 100%;
  padding: 12px;
  margin: 0;
  border: none;
  background: var(--White95);
  font-size: 15px;
  outline: none;
  font-weight: normal;
  color: var(--Graphite);
`

const RolesFilter = styled.div`
  position: absolute;
  margin: 44px 0 0;
`

const StyledList = styled.div`
  border-top: solid 1px var(--White90);
  height: calc(100vh - 360px);
  overflow-y: auto;
`

const BankUsersLayout = ({
  availableRoles,
  canDelete,
  canProceed,
  currentUser,
  errors,
  isFiltering,
  isNew,
  onChangeEmail,
  onChangeFirstName,
  onChangeLastName,
  onChangeRole,
  onChangeRoleFilter,
  onChangeSearchTerm,
  onChangeVerificationUser,
  onClickFilter,
  onDeleteUser,
  onSaveUser,
  onSelectUser,
  roleFilter,
  saveSuccess,
  searchTerm,
  selectedUser,
  serverErrors,
  setIsNew,
  totalNumUsers,
  users,
  verificationUsername
}: BankUsersLayoutProps) => {
  const filterBtnTextSuffix = roleFilter.length === 0 ? '(All)' : `(${totalNumUsers})`
  const filterBtnText = `Filter by role ${filterBtnTextSuffix}`
  return (
    <StyledContentSection>
      <MasterDetail
        detailChildren={
          <Section data-private type="quaternary">
            {(selectedUser || isNew) && (
              <UserDetailsPanel
                availableRoles={availableRoles}
                canDelete={canDelete}
                canProceed={canProceed}
                currentUser={currentUser}
                errors={errors}
                isNew={isNew}
                saveSuccess={saveSuccess}
                selectedUser={selectedUser}
                serverErrors={serverErrors}
                verificationUsername={verificationUsername}
                onChangeEmail={onChangeEmail}
                onChangeFirstName={onChangeFirstName}
                onChangeLastName={onChangeLastName}
                onChangeRole={onChangeRole}
                onChangeVerificationUser={onChangeVerificationUser}
                onDeleteUser={onDeleteUser}
                onSaveUser={onSaveUser}
              />
            )}
          </Section>
        }
        masterChildren={
          <Section data-private type="primary">
            <Space isflex flexDirection="column">
              <Space mt={6}>
                <StyledInput
                  type="text"
                  value={searchTerm}
                  onChange={v => onChangeSearchTerm(v.target.value)}
                />
              </Space>
            </Space>
            <Space isflex fontSize="15px" justifyContent="space-between" pt={6}>
              <FilterButton operationType="neutral" onClick={() => onClickFilter()}>
                {filterBtnText}
              </FilterButton>
              {isFiltering && (
                <RolesFilter>
                  <RolesSelector
                    filter={roleFilter}
                    roles={availableRoles}
                    onChangeRoleFilter={onChangeRoleFilter}
                  />
                </RolesFilter>
              )}
              <LinkIcon
                disabled={false}
                icon={<Icon name="circleAdd" size="default" />}
                iconPosition="left"
                text="Add a new Absa user"
                onClick={() => setIsNew()}
              />
            </Space>
            <Space isflex justifyContent="space-between" py={6}>
              <Typography fontSize="15px" fontWeight="800" letterSpacing="0px" variant="b3">
                {totalNumUsers} users found
              </Typography>
              <DownloadLink
                onClick={() =>
                  onDownloadLinkClick({
                    url: '/api/usermanager/export/bank'
                  })
                }
              >
                Export users to csv
              </DownloadLink>
            </Space>
            <StyledList>
              {users.map(user => (
                <BankUserCard
                  key={user.userId}
                  isSelected={selectedUser && selectedUser.userId === user.userId}
                  user={user}
                  onClickUser={onSelectUser}
                />
              ))}
            </StyledList>
          </Section>
        }
        width="normal"
      />
    </StyledContentSection>
  )
}

export default BankUsersLayout
